import { memo, useState } from "react";
import { Box, FlexColBox, Icon, Input } from "../../../common/components";
import { createStyles, palette, spacing } from "../../../styles/theme";
import { ComboItems, ItemPricing } from "../../../types";
import { Typography } from "@mui/material";
import sparkling from "../../../assets/images/sparkling_2.svg";
import { useTranslation } from "react-i18next";
import { getSizeQtyFromRecommendations } from "../../../utils/CartUtils";
import NonLinearSlider from "../../../common/components/Slider/NonLinearSlider";

const useStyles = (nbOfSizeContainer: number) =>
  createStyles({
    container: {
      background: `linear-gradient(90deg, rgba(84, 172, 222, 0.04) 0%, rgba(169, 84, 222, 0.04) 100%), ${palette.lightBorderGradient}`,
      borderRadius: "8px",
      p: spacing.groupingMobile,
      border: "1px solid transparent",
      gap: spacing.regularDesktop,
      display: "flex",
      flexDirection: "column",
    },
    bundleHeader: {
      p: spacing.groupingMobile,
      borderRadius: "4px 4px 0 0",
      background: palette.grey.lightBackground,
      overflow: "hidden",
    },
    sizeContainer: {
      display: "grid",
      alignItems: "center",
      gridTemplateColumns: {
        xs: `repeat(auto-fill,minmax(48px, 1fr))`,
        lg: `repeat(auto-fill,minmax(48px, 1fr))`,
      },
      flexWrap: "nowrap",
      columnGap: "4px",
      rowGap: "24px",
    },
  });

interface Props {
  item: ItemPricing;
  comboItems: ComboItems;
  baseQty: number;
  onClose: () => void;
}

const CartItemSizeRecommendation = ({
  item,
  baseQty,
  onClose,
  comboItems,
}: Props) => {
  const { t } = useTranslation();
  const isSocks = comboItems.familyName === "socks";
  const [totalQty, setTotalQty] = useState(
    baseQty > comboItems.quantityRequirements.minimumQuantity
      ? baseQty
      : comboItems.quantityRequirements.minimumQuantity
  );

  const styles = useStyles(
    isSocks
      ? (item.sizeRecommendations?.length || 0) + 1
      : item.sizeRecommendations?.length || 0
  );
  const sizeRecommendations = item.sizeRecommendations || [];
  const sizeQty = getSizeQtyFromRecommendations(
    isSocks
      ? sizeRecommendations.filter((s) => s.size !== "OneSize")
      : sizeRecommendations,
    totalQty,
    { minPerSize: item.quantityRequirements.minimumQuantityPerSize }
  );

  const getFormattedPercentage = (percentage: number) => {
    if (percentage < 1) {
      return `${percentage.toFixed(2)}%`;
    } else if (percentage < 5) {
      return `${percentage.toFixed(1)}%`;
    } else {
      return `${Math.round(percentage)}%`;
    }
  };

  return (
    <Box sx={styles.container}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <img src={sparkling} alt="recommend-sizes" />
          <Typography
            variant="textSm"
            sx={{
              background: "linear-gradient(90deg, #54ACDE 0%, #A954DE 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontWeight: 500,
            }}
          >
            {t("cart.recommend_sizes.section_title")}
          </Typography>
        </Box>
        <Box
          sx={{ cursor: "pointer" }}
          onClick={() => {
            onClose();
          }}
        >
          <Icon
            icon="close"
            size={16}
            color={palette.grey.nearBlack}
            weight={400}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-end", xl: "center" },
          gap: "4px",
          flexDirection: { xs: "column", xl: "row-reverse" },
        }}
      >
        <Input
          value={totalQty}
          containerSx={{
            width: "100px",
          }}
          type="number"
          onChange={(e) => {
            const value = Number(e.target.value);
            if (value > 20000) {
              setTotalQty(20000);
              return;
            }
            if (value < 0) {
              setTotalQty(0);
              return;
            }
            setTotalQty(value);
          }}
          noMaxWidth
          sx={{
            width: "100px",
          }}
          inputProps={{
            type: "number",
            min: 0,
            max: 20000,
            step: 1,
            sx: {
              textAlign: "right",
            },
          }}
        />
        <NonLinearSlider
          value={totalQty}
          ranges={[
            { min: 1, max: 20, weight: 13, step: 1 },
            { min: 20, max: 30, weight: 13, step: 1 },
            { min: 30, max: 50, weight: 13, step: 1 },
            { min: 50, max: 150, weight: 13, step: 5 },
            { min: 150, max: 500, weight: 13, step: 25 },
            { min: 500, max: 1500, weight: 13, step: 100 },
            { min: 1500, max: 5000, weight: 22, step: 500 },
          ]}
          onChange={(value) => {
            setTotalQty(value);
          }}
        />
      </Box>

      <Box>
        <Box display="flex" sx={styles.sizeContainer}>
          {item.sizeRecommendations?.map((size, index, array) => {
            const qty = sizeQty.find((s) => s.size === size.size)?.quantity;
            return (
              <>
                {isSocks && index === (array?.length || 0) - 1 && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                    }}
                  >
                    <Typography variant="textXs" color={palette.grey.primary}>
                      {t("cart.recommend_sizes.or")}
                    </Typography>
                  </Box>
                )}
                <Box
                  display="flex"
                  alignItems="center"
                  gap="8px"
                  flexDirection="column"
                >
                  <FlexColBox alignItems="center" fullWidth>
                    <Typography
                      variant="textRegularBold"
                      whiteSpace={"nowrap"}
                      minWidth={"fit-content"}
                    >
                      {t(`sizes.${size.size}`)}
                    </Typography>
                    <Typography
                      variant="actionBigIcon"
                      color={palette.grey.primary}
                      sx={{
                        px: 1.5,
                        py: 1,
                        height: 40,
                        maxWidth: spacing.xlMobile,
                        width: "100%",
                        borderRadius: "8px",
                        border: `1px solid ${palette.grey.lightGrey}`,
                        background: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {isSocks && index === (array?.length || 0) - 1
                        ? totalQty || 0
                        : qty || 0}
                    </Typography>
                  </FlexColBox>
                  <Typography variant="textXs" color={palette.grey.primary}>
                    {getFormattedPercentage(size.percentage)}
                  </Typography>
                </Box>
              </>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default memo(CartItemSizeRecommendation);
